import * as React from "react";
import { useLocation } from "@opendash/router";
import Parse from "parse";
import { useAppState } from "../state";
import { PageView } from "../types";
import { hasConsent, useConsent } from "./ConsentContext";
import { isAnalyticsEnabled } from "../config/analytics";

const VISITOR_KEY = "analytics_visitor_id";
const SESSION_KEY = "analytics_session_id";

function newId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

// IDs are created lazily, only when extended consent has been granted.
function getOrCreateSessionId(): string {
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = newId();
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return newId();
  }
}

function getOrCreateVisitorId(): string {
  try {
    let vid = window.localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      vid = newId();
      window.localStorage.setItem(VISITOR_KEY, vid);
    }
    return vid;
  } catch {
    return newId();
  }
}

// Coarse device classification — buckets in the millions, no fingerprinting
function deriveDeviceClass(ua: string): "mobile" | "tablet" | "desktop" {
  if (/tablet|ipad/i.test(ua)) return "tablet";
  if (/mobile|android|iphone/i.test(ua)) return "mobile";
  return "desktop";
}

function coarseLanguage(): string {
  const lang = (document.documentElement.lang || "de").toLowerCase();
  return lang.split("-")[0];
}

/**
 * Maps the current SPA route to a human-readable page name.
 * Invoice IDs and video IDs are resolved to their actual titles.
 *
 * Die Namen müssen sprachunabhängig sein, sonst zählt die Auswertung dieselbe
 * Seite pro Sprache getrennt. Die statischen Namen unten sind deshalb bewusst
 * fest auf Deutsch, auch wenn die Route eine lokalisierte Variante hat
 * (/contact und /kontakt ergeben beide "Kontakt").
 */
function useReadablePageName(): string {
  const location = useLocation();
  const { invoices, videos } = useAppState();
  const path = location.pathname;

  const invoiceMatch = path.match(/\/rechnungserklaerer\/rechnung\/([^/]+)/);
  if (invoiceMatch) {
    const invoice = invoices.find((i) => i.id === invoiceMatch[1]);
    if (invoice) {
      // Bewusst der rohe Titel statt getInvoiceTitle(): das würde je nach
      // Sprache "Strom" oder "Electricity" liefern und die Rechnung damit als
      // zwei verschiedene Seiten zählen.
      return `Rechnung / ${invoice.get("title") || invoice.id}`;
    }
    return "Rechnung";
  }

  const videoMatch = path.match(/\/rechnungserklaerer\/filme\/([^/]+)/);
  if (videoMatch) {
    const video = videos.find((v) => v.id === videoMatch[1]);
    if (video) {
      return `Film / ${video.get("title")}`;
    }
    return "Filme";
  }

  if (path === "/" || path === "/rechnungserklaerer/start") {
    return "Startseite";
  }
  if (path === "/rechnungserklaerer/rechnung") {
    return "Rechnungserklärer";
  }
  if (path === "/rechnungserklaerer/filme") {
    return "Filme";
  }
  if (path.startsWith("/rechnungserklaerer/glossar")) {
    return "Glossar";
  }
  if (
    path === "/rechnungserklaerer/contact" ||
    path === "/rechnungserklaerer/kontakt"
  ) {
    return "Kontakt";
  }
  if (
    path === "/rechnungserklaerer/energy-saving-tips" ||
    path === "/rechnungserklaerer/energiespartipps"
  ) {
    return "Energiespartipps";
  }
  if (path === "/impressum") return "Impressum";
  if (path === "/agb") return "AGB";
  if (path === "/datenschutz") return "Datenschutz";

  return path;
}

interface SaveResult {
  pv: Parse.Object;
  isExtended: boolean;
}

/**
 * Persist a single pageview / event.
 *
 * Always (Art. 6 Abs. 1 lit. f DSGVO):
 *   tenant, hostname, pageName, path, eventType, language (coarse),
 *   deviceClass (coarse), createdAt
 *
 * Additionally with consent (Art. 6 Abs. 1 lit. a DSGVO + § 25 TTDSG):
 *   visitorId, sessionId, userAgent, referrer, screenWidth, screenHeight
 *   (and later `duration` patched in by the duration handler)
 */
function savePageView(
  tenantId: string,
  pageName: string,
  path: string,
  eventType: string = "pageview",
  eventData?: Record<string, string | number>
): SaveResult | null {
  // Per-Tenant-Modus: Tracking nur, wenn der Tenant nicht auf "off" steht
  // (Default aus). Deckt beide Aufrufpfade (PageView + trackEvent) ab.
  if (!isAnalyticsEnabled()) return null;

  const extended = hasConsent();

  const pv = new PageView();
  pv.set("tenant", new Parse.Object("OD3_Tenant", { id: tenantId }) as any);
  pv.set("hostname", window.location.hostname);
  pv.set("pageName", pageName);
  pv.set("path", path);
  pv.set("eventType", eventType);
  pv.set("language", coarseLanguage());
  pv.set("deviceClass", deriveDeviceClass(navigator.userAgent));
  if (eventData) {
    pv.set("eventData", JSON.stringify(eventData));
  }

  if (extended) {
    pv.set("sessionId", getOrCreateSessionId());
    pv.set("visitorId", getOrCreateVisitorId());
    pv.set("userAgent", navigator.userAgent);
    pv.set("referrer", document.referrer);
    pv.set("screenWidth", window.screen.width);
    pv.set("screenHeight", window.screen.height);
  }

  pv.save().catch((err) => {
    console.warn("Analytics save failed:", err);
  });

  return { pv, isExtended: extended };
}

/**
 * Track a custom event (e.g. video play). Runs in anonymous or extended mode
 * depending on consent state.
 */
export function trackEvent(
  eventName: string,
  data?: Record<string, string | number>
) {
  if (!isAnalyticsEnabled()) return;

  const { app } = require("../state");
  const state = app.store.getState();
  const tenantId = state?.tenantId;
  if (!tenantId) return;

  savePageView(
    tenantId,
    data?.title ? String(data.title) : eventName,
    window.location.pathname,
    eventName,
    data
  );
}

export const AnalyticsTracker = React.memo(function AnalyticsTracker() {
  const location = useLocation();
  const { tenantId } = useAppState();
  const readableName = useReadablePageName();
  const { consent } = useConsent();
  const lastPageView = React.useRef<Parse.Object | null>(null);
  const lastTimestamp = React.useRef<number>(Date.now());

  const saveDuration = React.useCallback(() => {
    // Duration is only meaningful for extended pageviews — without an identifier
    // we cannot correlate enter/leave anyway.
    if (!hasConsent()) return;
    if (lastPageView.current) {
      const seconds = Math.round((Date.now() - lastTimestamp.current) / 1000);
      if (seconds > 0 && seconds < 3600) {
        lastPageView.current.set("duration", seconds);
        lastPageView.current.save().catch(() => {});
      }
    }
  }, []);

  // Window listeners — only useful while extended consent is granted
  React.useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    if (consent !== "granted") return;

    const handleUnload = () => saveDuration();
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") saveDuration();
    };
    window.addEventListener("beforeunload", handleUnload);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("beforeunload", handleUnload);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [consent, saveDuration]);

  // Drop cached extended pageview ref when consent is revoked.
  React.useEffect(() => {
    if (consent !== "granted") {
      lastPageView.current = null;
    }
  }, [consent]);

  // Track pageview on every route change. Note: we deliberately do NOT depend
  // on `consent` here so that consent transitions don't double-log the same
  // page. The consent state is read inside savePageView via hasConsent().
  React.useEffect(() => {
    if (!isAnalyticsEnabled()) return;
    if (!tenantId) return;

    // Debounce: Die Rechnungs-Route leitet beim Öffnen in schneller Folge um
    // (/rechnung → /rechnung/<id> → /rechnung/<id>/<pageId>). Ohne Debounce
    // würde jede Zwischen-URL als eigener PageView gezählt (Doppelzählung).
    // So wird nur die final anstehende Route innerhalb des Fensters erfasst.
    const timer = setTimeout(() => {
      saveDuration();

      const result = savePageView(
        tenantId,
        readableName,
        location.pathname,
        "pageview"
      );

      if (result?.isExtended) {
        lastPageView.current = result.pv;
        lastTimestamp.current = Date.now();
      } else {
        lastPageView.current = null;
        lastTimestamp.current = Date.now();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [location.pathname, tenantId, readableName, saveDuration]);

  return null;
});
